import { useState } from 'react';
import { Alert, App, Button, Input, Modal, Space, Tag, Typography } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { formatHandle } from '../../lib/listenerName';

/** The API's minimum for the audit reason (`POST …/verification-admin`). */
export const VERIFICATION_REASON_MIN = 10;

/**
 * "Call Verification Admin" flag on a user: a flagged account's calls are
 * verification calls — the caller pays 0 coins, the listener earns ₹0, no
 * ledger rows, gifts disabled. Only call START reads the flag, so flipping it
 * never touches an in-flight call.
 *
 * Shows the current state and, for `users.verification_admin` holders, a
 * reason-gated enable/disable modal (same shape as block-with-reason).
 */
export function VerificationAdminControl({
  userId,
  handle,
  label,
  enabled,
  setAt,
  onChanged,
}: {
  userId: string;
  handle: string;
  /** How to name the user in the confirm title ("Ravi (@ravi)"); defaults to the @handle. */
  label?: string;
  enabled: boolean;
  setAt: string | null;
  /** Called after a successful change so the owner refetches the user. */
  onChanged: () => void;
}) {
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const next = !enabled;

  function close() {
    setOpen(false);
    setReason('');
  }

  const set = useMutation({
    mutationFn: async (body: { enabled: boolean; reason: string }) => {
      await api.post(`/admin/users/${userId}/verification-admin`, body);
    },
    onSuccess: () => {
      message.success(next ? 'Verification calls enabled' : 'Verification calls disabled');
      close();
      onChanged();
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ??
        'Could not update call-verification admin';
      message.error(msg);
    },
  });

  return (
    <>
      <Space size={8} wrap>
        <Tag color={enabled ? 'purple' : 'default'}>{enabled ? 'Enabled' : 'Disabled'}</Tag>
        {enabled && setAt ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Set at: {new Date(setAt).toLocaleString()}
          </Typography.Text>
        ) : null}
        <Can permission="users.verification_admin">
          <Button size="small" onClick={() => setOpen(true)}>
            {next ? 'Enable verification calls' : 'Disable verification calls'}
          </Button>
        </Can>
      </Space>

      <Modal
        title={`${next ? 'Enable' : 'Disable'} verification calls for ${label ?? formatHandle(handle) ?? 'this user'}?`}
        open={open}
        onCancel={close}
        onOk={() => set.mutate({ enabled: next, reason: reason.trim() })}
        okText={next ? 'Enable' : 'Disable'}
        okButtonProps={{
          disabled: reason.trim().length < VERIFICATION_REASON_MIN,
          loading: set.isPending,
        }}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={next ? 'Calls from this account will not be billed' : 'Calls from this account will be billed again'}
          description={
            <span style={{ fontSize: 12 }}>
              Verification calls charge the caller 0 coins, pay the listener ₹0, write no ledger rows and
              have gifts disabled. Applies to calls started after this change. An active call is not
              affected.
            </span>
          }
        />
        <Input.TextArea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={`Reason (≥ ${VERIFICATION_REASON_MIN} chars) — captured in the audit log.`}
        />
      </Modal>
    </>
  );
}
